<template>
  <div class="news">
    <!-- 顶部 Banner：背景图（1920x690，向下渐隐）+ 文字居中 -->
    <div class="news-banner">
      <div class="news-banner-bg"></div>
      <div class="news-banner-inner">
        <div class="news-banner-title">资讯中心</div>
        <div class="news-banner-sub">分享节点小宝最新企业资讯以及行业前沿技术</div>
      </div>
    </div>

    <!-- 主体 -->
    <div class="news-main">
      <!-- 分类筛选 -->
      <div class="news-cats">
        <div
          v-for="cat in catList"
          :key="cat.id"
          class="news-cat"
          :class="{ 'news-cat-active': activeCat === cat.id }"
          @click="switchCat(cat.id)"
        >
          {{ cat.name }}
        </div>
      </div>

      <!-- 加载中 -->
      <div v-if="loading" class="news-loading">加载中...</div>

      <!-- 文章列表 -->
      <div v-else-if="list.length" class="news-grid">
        <div
          v-for="post in list"
          :key="post.id"
          class="news-card"
          @click="goDetail(post)"
        >
          <!-- 封面 -->
          <div class="news-card-cover">
            <img v-if="post.cover" :src="post.cover" :alt="post.title" loading="lazy" />
            <div v-else class="news-card-cover-placeholder">节点小宝</div>
          </div>
          <!-- 文字区 -->
          <div class="news-card-body">
            <div class="news-card-title">{{ post.title }}</div>
            <div class="news-card-excerpt">{{ post.excerpt }}</div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="news-empty">暂无资讯</div>

      <!-- 分页 -->
      <div v-if="totalPages > 1" class="news-pager">
        <div class="news-page-arrow" :class="{ 'news-page-arrow-disabled': page <= 1 }" @click="goPage(page - 1)">
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 1L2 8L9 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="news-pages">
          <div
            v-for="(pageItem, i) in pageItems"
            :key="i"
            class="news-page-num"
            :class="{ 'news-page-num-active': pageItem === page, 'news-page-more': pageItem === '...' }"
            @click="clickPage(pageItem, i)"
          >
            <!-- 省略号：默认三点图标，悬浮时切换为双箭头（与 element 分页一致） -->
            <template v-if="pageItem === '...'">
              <svg class="more-dots" width="16" height="4" viewBox="0 0 16 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="2" cy="2" r="2" fill="currentColor"/>
                <circle cx="8" cy="2" r="2" fill="currentColor"/>
                <circle cx="14" cy="2" r="2" fill="currentColor"/>
              </svg>
              <!-- 双箭头基准为左向 «，右省略号通过镜像翻转成右向 »（element 分页交互） -->
              <svg class="more-arrows" :class="{ 'more-arrows-flip': !isLeftMore(i) }" width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M15 1L9 7L15 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </template>
            <template v-else>{{ pageItem }}</template>
          </div>
        </div>
        <div class="news-page-arrow" :class="{ 'news-page-arrow-disabled': page >= totalPages }" @click="goPage(page + 1)">
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L8 8L1 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
    </div>

    <Foot></Foot>
  </div>
</template>

<script>
import { getPosts, getCategories } from "../req/wp";
import Foot from "@/components/Foot.vue";

export default {
  name: "News",
  components: {
    Foot,
  },
  data: function () {
    return {
      // 分类筛选：第一项"全部"是前端加的，id=0 表示不筛选
      catList: [{ id: 0, name: "全部" }],
      activeCat: 0,
      list: [],
      page: 1,
      totalPages: 1,
      loading: false,
    };
  },
  computed: {
    // 页码窗口：生成分页条上要显示的数字序列，省略号代表中间被折叠的页码。
    // 策略：总页数少时全部显示；页数多时只保留 [首页, 当前页前后各1页, 尾页]，
    //       其余页码折叠成省略号。
    pageItems: function () {
      // ---------- 窗口策略参数 ----------
      // 总页数不超过该值时全部显示，不做折叠
      const MAX_VISIBLE_PAGES = 7;
      // 当前页两侧各连续保留的页码数（1 表示前后各显示一页）
      const NEIGHBOR_COUNT = 1;

      const total = this.totalPages;
      const cur = this.page;
      const pages = [];

      // 总页数少，全部显示
      if (total <= MAX_VISIBLE_PAGES) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
        return pages;
      }

      // 首页固定显示
      pages.push(1);

      // 左侧省略号条件：首页与当前页连续区之间真的藏了页码才显示，
      // 即 当前页左侧可隐藏的页数 >= 1（cur - 邻居页 - 首页 - 1 >= 0 整理而来）
      if (cur - NEIGHBOR_COUNT >= 3) {
        pages.push("...");
      }

      // 当前页附近连续显示的页码范围：
      //   左边界：当前页往前 NEIGHBOR_COUNT 页，但不低于第 2 页（第 1 页已单独显示）
      //   右边界：当前页往后 NEIGHBOR_COUNT 页，但不超过倒数第 2 页（尾页已单独显示）
      const from = Math.max(2, cur - NEIGHBOR_COUNT);
      const to = Math.min(total - 1, cur + NEIGHBOR_COUNT);
      for (let i = from; i <= to; i++) {
        pages.push(i);
      }

      // 右侧省略号条件：与左侧对称，当前页右侧可隐藏的页数 >= 1
      if (total - cur - NEIGHBOR_COUNT >= 2) {
        pages.push("...");
      }

      // 尾页固定显示
      pages.push(total);

      return pages;
    },
  },
  created: function () {
    this.loadCats();
    this.loadPosts();
  },
  methods: {
    // 加载分类数据，第一项固定"全部"，其余来自 WordPress 分类接口
    loadCats: function () {
      getCategories()
        .then((cats) => {
          const rest = (cats || []).map((category) => ({
            id: category.id,
            name: category.name,
          }));
          this.catList = [{ id: 0, name: "全部" }].concat(rest);
        })
        .catch((err) => {
          console.error("资讯分类加载失败", err);
        });
    },
    // 按当前页码和分类加载文章列表
    loadPosts: function () {
      this.loading = true;
      getPosts({
        page: this.page,
        category: this.activeCat || undefined,
      })
        .then((res) => {
          this.list = res.list;
          this.totalPages = res.totalPages;
        })
        .catch((err) => {
          console.error("资讯列表加载失败", err);
          this.list = [];
          this.totalPages = 1;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // 点击页码：省略号与 element 分页一致，快进/快退一屏
    clickPage: function (page, index) {
      // 省略号跳转步数：与 element-ui pager 一致（pagerCount 7 - 2 = 5）
      const MORE_OFFSET = 5;
      if (page === "...") {
        // element 行为：越界时到达首尾页（而非不跳），既未越界则正常跳转
        const currentIndex = this.pageItems.indexOf(this.page);
        let target = index < currentIndex ? this.page - MORE_OFFSET : this.page + MORE_OFFSET;
        if (target < 1) {
          target = 1;
        }
        if (target > this.totalPages) {
          target = this.totalPages;
        }
        if (target !== this.page) {
          this.goPage(target);
        }
        return;
      }
      this.goPage(page);
    },
    // 切换分类：重置到第 1 页并重新加载列表
    switchCat: function (id) {
      if (this.activeCat === id) {
        return;
      }
      this.activeCat = id;
      this.page = 1;
      this.loadPosts();
    },
    // 判断省略号是否位于当前页左侧（用于 hover 双箭头方向）
    isLeftMore: function (index) {
      const currentIndex = this.pageItems.indexOf(this.page);
      return index < currentIndex;
    },
    // 跳转指定页：越界或重复点击时忽略，翻页后回到页面顶部
    goPage: function (page) {
      if (page < 1 || page > this.totalPages || page === this.page) {
        return;
      }
      this.page = page;
      this.loadPosts();
      window.scrollTo(0, 0);
    },
    // 进入文章详情页
    goDetail: function (post) {
      this.$router.push("/news/detail/" + post.id);
    },
  },
};
</script>

<style scoped lang="less">
.news {
  min-height: 600px;
  background: #f7f9fe;

  /* ---------- 顶部 Banner（高 330，光晕渐变背景，文字距顶 126） ---------- */
  .news-banner {
    position: relative;
    height: 330px;
    width: 100%;
    // 背景图：浅蓝渐变光晕（2x 3840x1380），cover 铺满，
    // 底部叠一层渐变过渡到页面背景色，做出光晕渐隐的效果
    .news-banner-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(180deg, rgba(247, 249, 254, 0) 75%, #f7f9fe 100%),
        url("../assets/news_banner.jpg") no-repeat center center / cover;
    }
    .news-banner-inner {
      position: relative;
      z-index: 1;
      padding-top: 126px;
      text-align: center;
    }
    .news-banner-title {
      font-family: "Source Han Sans CN", "Microsoft Yahei", "微软雅黑", sans-serif;
      font-size: 48px;
      font-weight: 700;
      line-height: 68px;
      color: #1d1f24;
    }
    .news-banner-sub {
      margin-top: 15px;
      font-family: "Source Han Sans CN", "Microsoft Yahei", "微软雅黑", sans-serif;
      font-size: 20px;
      font-weight: 400;
      line-height: 28px;
      color: #1d1f24;
    }
  }

  /* ---------- 主体容器 ---------- */
  .news-main {
    width: 1280px;
    margin: 0 auto;
    padding-bottom: 80px;
    box-sizing: border-box;
  }

  /* ---------- 分类筛选 ---------- */
  .news-cats {
    display: flex;
    gap: 12px;
    margin-bottom: 40px;
    .news-cat {
      padding: 8px 24px;
      font-family: "Source Han Sans CN", "Microsoft Yahei", "微软雅黑", sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #57576c;
      background: #ffffff;
      border-radius: 20px;
      cursor: pointer;
      transition: all 0.2s;
      &:hover {
        color: #3772ff;
      }
      &.news-cat-active {
        color: #ffffff;
        background: #3772ff;
      }
    }
  }

  /* ---------- 文章卡片列表 ---------- */
  .news-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 40px 40px;
  }
  .news-card {
    background: #ffffff;
    border-radius: 20px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(23, 33, 94, 0.08);
      .news-card-title {
        color: #3772ff; /* 标题 hover 变蓝 */
      }
    }

    /* 封面 */
    .news-card-cover {
      position: relative;
      width: 100%;
      padding-top: 55%; /* 400:220 */
      overflow: hidden;
      background: #f0f2f8;
      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .news-card-cover-placeholder {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 30px;
        font-weight: 700;
        color: #ffffff;
        background: linear-gradient(135deg, #2f6bff 0%, #4e4cff 60%, #6a5cff 100%);
      }
    }

    /* 文字区 */
    .news-card-body {
      padding: 23px 18px 20px;
      .news-card-title {
        font-family: "Source Han Sans CN", "Microsoft Yahei", "微软雅黑", sans-serif;
        font-size: 20px;
        font-weight: 500;
        line-height: 30px;
        color: #17171b;
        transition: color 0.2s;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
      }
      .news-card-excerpt {
        margin-top: 10px;
        font-family: "Source Han Sans CN", "Microsoft Yahei", "微软雅黑", sans-serif;
        font-size: 14px;
        font-weight: 350;
        line-height: 22px;
        color: #57576c;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
      }
    }
  }

  /* ---------- 加载 / 空状态 ---------- */
  .news-loading {
    padding: 80px 0;
    text-align: center;
    font-size: 16px;
    color: #999da6;
  }
  .news-empty {
    padding: 100px 0;
    text-align: center;
    font-size: 16px;
    color: #999da6;
  }

  /* ---------- 分页 ---------- */
  .news-pager {
    margin-top: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    // 箭头与页码组间距：配合箭头容器左右各 10px 内空，视觉间距约 16px（10px偏挤、20px偏远）
    gap: 6px;

    // 箭头：外框 36x36 与页码按钮同大，保证点击热区足够大；图标仍为 16x16 视觉尺寸
    .news-page-arrow {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #252a3a;
      cursor: pointer;
      transition: color 0.2s;
      &:hover {
        color: #3772ff;
      }
      &.news-page-arrow-disabled {
        color: #aeb2be;
        cursor: not-allowed;
        &:hover {
          color: #aeb2be;
        }
      }
    }

    // 页码之间 10px
    .news-pages {
      display: flex;
      gap: 10px;
    }

    .news-page-num {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: "Source Han Sans CN", "Microsoft Yahei", "微软雅黑", sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #252a3a;
      background: #ffffff;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
      &:hover {
        color: #3772ff;
      }
      &.news-page-num-active {
        color: #ffffff;
        background: #3772ff;
      }

      // 省略号：默认三点图标垂直居中，悬浮时显示双箭头（element 分页交互）
      &.news-page-more {
        .more-arrows {
          display: none;
        }
        // 右侧省略号 hover 时镜像成右向双箭头 »
        .more-arrows-flip {
          transform: scaleX(-1);
        }
        &:hover {
          .more-dots {
            display: none;
          }
          .more-arrows {
            display: block;
          }
        }
      }
    }
  }
}
</style>
